import { ReactNode, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IoMdClose } from 'react-icons/io';
import cx from 'classnames';
import { Speeds } from '../../styles';
import Card from '../Card';
import IconButton from '../IconButton';
import styles from './Popup.module.scss';

export interface PopupProps {
  onClose?: Function;
  children?: ReactNode;
  speed?: Speeds;
  className?: string;
  show: boolean;
}

function Popup({
  speed = Speeds.NORMAL,
  onClose = () => {},
  show = false,
  className,
  children,
}: PopupProps) {
  const [showProxy, setShowProxy] = useState(show);
  const _onClose = useRef<PopupProps['onClose']>();
  const target = useRef<HTMLElement>();
  _onClose.current = onClose;

  const isMounted = show || showProxy;
  const isShown = show && showProxy;

  useEffect(() => {
    const action = () =>
      window.requestAnimationFrame(() => {
        setShowProxy(show);
      });
    const timer = window.setTimeout(action, show ? 0 : speed);
    return () => {
      window.clearTimeout(timer);
      action();
    };
  }, [show, speed]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!target.current) return;
      if (target.current.contains(e.target as any)) {
        return;
      }
      _onClose.current?.();
    };

    const ref = window.requestAnimationFrame(() => {
      document.documentElement.classList.toggle('blur', isShown);
    });
    window.addEventListener('click', handler, true);
    return () => {
      window.removeEventListener('click', handler, true);
      window.cancelAnimationFrame(ref);
    };
  }, [isShown]);

  return createPortal(
    isMounted ? (
      <Card
        ref={target}
        className={cx(className, styles.container)}
        backgroundColor="background-1"
        padding="5.5rem"
      >
        {children}
        <IconButton
          onClick={onClose}
          className={styles.close}
          icon={IoMdClose}
          alt="Close popup"
        />
      </Card>
    ) : null,
    document.getElementById('popup')!,
  );
}

export default Popup;
